import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {createRef, CSSProperties, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import BoxesDialogFields from "../../field/page/BoxesDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import PdfViewer, {PdfViewerDefinition, PreviewState} from "../../../viewer/pdf/PdfViewer";
import {ViewerMode} from "../../../viewer/ViewerMode";
import {PdfPageDefinition} from "../../../viewer/pdf/PdfPage";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	BoxesResize,
	BoxesResizeInterface,
	BoxesToolboxOptions,
	DimensionsBoxesResize,
	Functions,
	Information,
	MetadataRectangle,
	Metrics,
	PageBox,
	PagePages,
	RemoveBoxes,
	RemoveBoxesInterface,
	ResizeBoxes,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {PortalToolboxOptionsOptions} from "../../../../shared/models/PortalToolboxOptionsOptions";
import {BoxesFunction, PortalBoxesToolboxOptions} from "../../../../shared/models/PortalBoxesToolboxOptions";
import Immutable from "../../../../shared/immutable/Immutable";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface BoxesDialogProperties extends PortalDialogProperties<PortalToolboxOptionsOptions>,
	PortalDocumentManagerGlobals {
	userSettings: Immutable<UserSettings>;
}

interface BoxesDialogStatus extends PortalDialogState<PortalToolboxOptionsOptions>, PreviewState {
	pages: number;
	currentPage: string;
	unit: LengthUnit;
}

export class BoxesDialogDefinition extends PortalDialog<PortalToolboxOptionsOptions, BoxesDialogProperties,
	BoxesDialogStatus> {
	private mediaPreview: RefObject<HTMLDivElement>;
	private cropPreview: RefObject<HTMLDivElement>;
	private bleedPreview: RefObject<HTMLDivElement>;
	private trimPreview: RefObject<HTMLDivElement>;
	private artPreview: RefObject<HTMLDivElement>;

	constructor(props: any) {
		super(props);

		this.mediaPreview = createRef<HTMLDivElement>();
		this.cropPreview = createRef<HTMLDivElement>();
		this.bleedPreview = createRef<HTMLDivElement>();
		this.trimPreview = createRef<HTMLDivElement>();
		this.artPreview = createRef<HTMLDivElement>();

		this.state = {
			...this.state,
			previewZoomValue: 0,
			renderPreview: false,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<BoxesDialogFields, string>(),
			unit: LengthUnit.MILLIMETER
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editBoxes, Functions.getEditBoxesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.BOXES, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private getBoxParameter(type: PageBox): ResizeBoxes | undefined {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;

		for (let boxesParameter of FunctionHelper.applyValue(
			parameter.resize, BoxesToolboxOptions.getResizeDefault()
		)) {
			if (boxesParameter.boxesResize?.box === type) {
				return boxesParameter;
			}
		}
	}

	private async getBoxDimensions(type: PageBox): Promise<BoxesResize> {
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let page: PagePages | undefined = portalDocument.getPage(documentSettings.activeViewer.currentPage);
		let pageCount: number = await portalDocument.getNumberOfPages();
		let pageSelection: string = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);

		let boxType: MetadataRectangle | undefined;
		switch (type) {
			case PageBox.MediaBox:
				boxType = page?.boxes?.mediaBox;
				break;
			case PageBox.ArtBox:
				boxType = page?.boxes?.artBox;
				break;
			case PageBox.TrimBox:
				boxType = page?.boxes?.trimBox;
				break;
			case PageBox.CropBox:
				boxType = page?.boxes?.cropBox;
				break;
			case PageBox.BleedBox:
				boxType = page?.boxes?.bleedBox;
				break;
		}

		let pageHeight: number = FunctionHelper.applyValue(
			boxType?.height, page?.height, MetadataRectangle.getHeightDefault()
		);
		let pageWidth: number = FunctionHelper.applyValue(
			boxType?.width, page?.width, MetadataRectangle.getWidthDefault()
		);
		let posX: number = FunctionHelper.applyValue(boxType?.x, MetadataRectangle.getXDefault());
		let posY: number = FunctionHelper.applyValue(boxType?.y, MetadataRectangle.getYDefault());

		return BoxesResize.fromJson({
			dimensions: {
				height: UnitHelper.unitToUnit(
					pageHeight, LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
				),
				width: UnitHelper.unitToUnit(
					pageWidth, LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
				),
				x: UnitHelper.unitToUnit(
					posX, LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
				),
				y: UnitHelper.unitToUnit(
					posY, LengthUnit.POINTS, LengthUnit.MILLIMETER, this.props.portalResolution
				),
				metrics: Metrics.Mm
			},
			pages: pageSelection,
			box: type
		} as BoxesResizeInterface);
	}

	private computeBoxStyle(previewScale: number, rotation: number, dimensions: DimensionsBoxesResize): CSSProperties {
		let width: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(dimensions.width, DimensionsBoxesResize.getWidthDefault()),
			LengthUnit.MILLIMETER, this.props.portalResolution
		);
		let height: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(dimensions.height, DimensionsBoxesResize.getHeightDefault()),
			LengthUnit.MILLIMETER, this.props.portalResolution
		);
		let x: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(dimensions.x, DimensionsBoxesResize.getXDefault()),
			LengthUnit.MILLIMETER, this.props.portalResolution
		);
		let y: number = UnitHelper.unitToPx(
			FunctionHelper.applyValue(dimensions.y, DimensionsBoxesResize.getYDefault()),
			LengthUnit.MILLIMETER, this.props.portalResolution
		);

		let style: CSSProperties = {
			width: previewScale * width + LengthUnit.PIXEL,
			height: previewScale * height + LengthUnit.PIXEL,
			left: previewScale * x + LengthUnit.PIXEL,
			bottom: previewScale * y + LengthUnit.PIXEL
		}

		switch (rotation) {
			case 1:
				style = {
					width: previewScale * height + LengthUnit.PIXEL,
					height: previewScale * width + LengthUnit.PIXEL,
					left: previewScale * y + LengthUnit.PIXEL,
					top: previewScale * x + LengthUnit.PIXEL
				}
				break;
			case 2:
				style = {
					width: previewScale * width + LengthUnit.PIXEL,
					height: previewScale * height + LengthUnit.PIXEL,
					top: previewScale * y + LengthUnit.PIXEL,
					right: previewScale * x + LengthUnit.PIXEL
				}
				break;
			case 3:
				style = {
					width: previewScale * height + LengthUnit.PIXEL,
					height: previewScale * width + LengthUnit.PIXEL,
					right: previewScale * y + LengthUnit.PIXEL,
					bottom: previewScale * x + LengthUnit.PIXEL
				}
				break;
		}

		return style;
	}

	public renderPreview(): ReactNode {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;
		let selected: PortalDocumentInterface | undefined = this.props.documentSelection.get().pop();

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(selected) ||
			parameter.mode === BoxesFunction.REMOVE) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(selected!.id!);
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let page: PagePages | undefined = portalDocument.getPage(documentSettings.activeViewer.currentPage);
		let previewScale: number = this.state.previewZoomValue / 100;

		let mediaboxParameter: ResizeBoxes | undefined = this.getBoxParameter(PageBox.MediaBox);
		let cropboxParameter: ResizeBoxes | undefined = this.getBoxParameter(PageBox.CropBox);
		let bleedboxParameter: ResizeBoxes | undefined = this.getBoxParameter(PageBox.BleedBox);
		let trimboxParameter: ResizeBoxes | undefined = this.getBoxParameter(PageBox.TrimBox);
		let artboxParameter: ResizeBoxes | undefined = this.getBoxParameter(PageBox.ArtBox);

		let rotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);
		let pageWidth: number = 0;
		let pageHeight: number = 0;

		switch (rotation) {
			case 0:
			case 2:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, PagePages.getWidthDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, PagePages.getHeightDefault()
				);
				break;
			case 1:
			case 3:
				pageWidth = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.height, page?.height, PagePages.getHeightDefault()
				);
				pageHeight = FunctionHelper.applyValue(
					page?.boxes?.cropBox?.width, page?.width, PagePages.getWidthDefault()
				);
				break;
		}

		pageWidth = Math.round(
			UnitHelper.unitToPx(pageWidth, LengthUnit.POINTS, this.props.portalResolution) * previewScale
		);
		pageHeight = Math.round(
			UnitHelper.unitToPx(pageHeight, LengthUnit.POINTS, this.props.portalResolution) * previewScale
		);

		return (
			<>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left">
					<div className="dialog-preview" style={{
						maxWidth: pageWidth <= 0 ? "100%" : pageWidth,
						maxHeight: pageHeight <= 0 ? "100%" : pageHeight
					}}>
						<PdfViewer {...PdfViewerDefinition.defaults()} {...this.props}
								   viewerMode={ViewerMode.PAGE} portalDocument={Immutable.from(selected!)}
								   currentPage={documentSettings.activeViewer.currentPage}
								   updatePreviewState={this.setState.bind(this)}/>
						<div className="preview-layer">
							<div className="mediabox" ref={this.mediaPreview} style={this.computeBoxStyle(
								previewScale, rotation, mediaboxParameter!.boxesResize!.dimensions!
							)}/>
							<div className="cropbox" ref={this.cropPreview} style={this.computeBoxStyle(
								previewScale, rotation, cropboxParameter!.boxesResize!.dimensions!
							)}/>
							<div className="bleedbox" ref={this.bleedPreview} style={this.computeBoxStyle(
								previewScale, rotation, bleedboxParameter!.boxesResize!.dimensions!
							)}/>
							<div className="trimbox" ref={this.trimPreview} style={this.computeBoxStyle(
								previewScale, rotation, trimboxParameter!.boxesResize!.dimensions!
							)}/>
							<div className="artbox" ref={this.artPreview} style={this.computeBoxStyle(
								previewScale, rotation, artboxParameter!.boxesResize!.dimensions!
							)}/>
						</div>
					</div>
				</div>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;
		let errors: Map<BoxesDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.type)) {
			return null;
		}

		let boxParameter: ResizeBoxes | undefined = this.getBoxParameter(parameter.type!);

		return (
			<>
				<div
					className={"p-col-12 p-grid" +
						(parameter?.mode === BoxesFunction.REMOVE ? "" : " p-xl-8 divider-right-hidden")
					}>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.MODE)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-mode">
								<Translate {...this.props} data="boxes.mode.type.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-mode"
										  value={FunctionHelper.applyValue(
											  parameter.mode, PortalBoxesToolboxOptions.getModeDefault()
										  )}
										  options={[
											  {
												  label: Translate.value(
													  "boxes.mode.type.edit", {ns: "dialog"}
												  ),
												  value: BoxesFunction.EDIT
											  },
											  {
												  label: Translate.value(
													  "boxes.mode.type.remove", {ns: "dialog"}
												  ),
												  value: BoxesFunction.REMOVE
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  BoxesDialogFields.MODE, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>

						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.TYPE)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-type">
								<Translate {...this.props} data="boxes.mode.box.label" namespace="dialog"/>
								<Info {...this.props} data="boxes.mode.box.info" options={{ns: "dialog"}}
									  markdown={true}/>
							</label>
							<div className="p-col-12 p-sm-7 p-grid">
								<Dropdown inputId="control-type"
										  value={FunctionHelper.applyValue(
											  parameter.type, PortalBoxesToolboxOptions.getTypeDefault()
										  )}
										  options={[
											  {
												  label: Translate.value(
													  "boxes.mode.box.media", {ns: "dialog"}
												  ),
												  value: PageBox.MediaBox
											  },
											  {
												  label: Translate.value(
													  "boxes.mode.box.crop", {ns: "dialog"}
												  ),
												  value: PageBox.CropBox
											  },
											  {
												  label: Translate.value(
													  "boxes.mode.box.bleed", {ns: "dialog"}
												  ),
												  value: PageBox.BleedBox
											  },
											  {
												  label: Translate.value(
													  "boxes.mode.box.trim", {ns: "dialog"}
												  ),
												  value: PageBox.TrimBox
											  },
											  {
												  label: Translate.value(
													  "boxes.mode.box.art", {ns: "dialog"}
												  ),
												  value: PageBox.ArtBox
											  }
										  ]}
										  onChange={(e) => this.onChange(
											  BoxesDialogFields.TYPE, e.value
										  )}
										  appendTo={document.getElementById("root")}/>
							</div>
						</div>
					</fieldset>

					<fieldset className="p-col-12 p-grid" hidden={parameter.mode === BoxesFunction.REMOVE}>
						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.MARGIN_LEFT)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-left">
								<Translate {...this.props} data="boxes.dimension.x" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-left" showButtons={true} useGrouping={false}
												 maxFractionDigits={2} minFractionDigits={2}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 boxParameter?.boxesResize?.dimensions?.x,
														 DimensionsBoxesResize.getXDefault()
													 ),
													 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
												 )}
												 min={DimensionsBoxesResize.getXMin()}
												 suffix={" " + this.state.unit} step={1}
												 onChange={(e) => this.onChange(
													 BoxesDialogFields.MARGIN_LEFT, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close" tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													BoxesDialogFields.MARGIN_LEFT,
													DimensionsBoxesResize.getXDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>

						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.MARGIN_TOP)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-margin-top">
								<Translate {...this.props} data="boxes.dimension.y" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7">
								<div className="p-inputgroup">
									<InputNumber inputId="control-margin-top" showButtons={true} useGrouping={false}
												 maxFractionDigits={2} minFractionDigits={2}
												 value={UnitHelper.unitToUnit(
													 FunctionHelper.applyValue(
														 boxParameter?.boxesResize?.dimensions?.y,
														 DimensionsBoxesResize.getYDefault()
													 ),
													 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
												 )}
												 min={DimensionsBoxesResize.getYMin()}
												 suffix={" " + this.state.unit} step={1}
												 onChange={(e) => this.onChange(
													 BoxesDialogFields.MARGIN_TOP, e.value
												 )}/>
									<div className="p-inputgroup-addon vertical">
										<Button icon="icon icon-close"
												tooltip={Translate.value("inputs.clear")}
												onClick={() => this.onChange(
													BoxesDialogFields.MARGIN_TOP, DimensionsBoxesResize.getYDefault()
												)}/>
									</div>
								</div>
							</div>
						</div>

						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.WIDTH)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-width">
								<Translate {...this.props} data="boxes.dimension.width" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7"
								 hidden={!this.state.fields?.contains(BoxesDialogFields.WIDTH)}>
								<InputNumber inputId="control-width" showButtons={true} useGrouping={false}
											 maxFractionDigits={2} minFractionDigits={2}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 boxParameter?.boxesResize?.dimensions?.width,
													 DimensionsBoxesResize.getWidthDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 min={DimensionsBoxesResize.getWidthMin()}
											 suffix={" " + this.state.unit} step={1}
											 onChange={(e) => this.onChange(
												 BoxesDialogFields.WIDTH, e.value
											 )}/>
							</div>
						</div>

						<div className="p-col-12 p-lg-6 p-grid"
							 hidden={!this.state.fields?.contains(BoxesDialogFields.HEIGHT)}>
							<label className="p-col-12 p-sm-5" htmlFor="control-height">
								<Translate {...this.props} data="boxes.dimension.height" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-7"
								 hidden={!this.state.fields?.contains(BoxesDialogFields.HEIGHT)}>
								<InputNumber inputId="control-height" showButtons={true} useGrouping={false}
											 maxFractionDigits={2} minFractionDigits={2}
											 value={UnitHelper.unitToUnit(
												 FunctionHelper.applyValue(
													 boxParameter?.boxesResize?.dimensions?.height,
													 DimensionsBoxesResize.getHeightDefault()
												 ),
												 LengthUnit.MILLIMETER, this.state.unit, this.props.portalResolution
											 )}
											 min={DimensionsBoxesResize.getHeightMin()}
											 suffix={" " + this.state.unit} step={1}
											 onChange={(e) => this.onChange(
												 BoxesDialogFields.HEIGHT, e.value
											 )}/>
							</div>
						</div>
					</fieldset>

					<fieldset className="p-col-12 p-grid"
							  hidden={!this.state.fields?.contains(BoxesDialogFields.PAGERANGE)}>
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-pagerange">
								<Translate {...this.props} data="boxes.pages.label" namespace="dialog"/>
								<Info {...this.props} data="boxes.pages.info" options={{ns: "dialog"}} markdown={true}/>
							</label>
							<div className="p-col-12 p-sm-7">
								<PageRange inputId="control-pagerange" editable={true}
										   className={errors.has(BoxesDialogFields.PAGERANGE) ? " p-invalid" : ""}
										   value={FunctionHelper.applyValue(
											   boxParameter?.boxesResize?.pages, BoxesResize.getPagesDefault()
										   )}
										   onChange={(e) => this.onChange(
											   BoxesDialogFields.PAGERANGE, e.value
										   )}
										   appendTo={document.getElementById("root")}
										   addon={Translate.value(
											   "inputs.pagerange.of", {pages: this.state.pages}
										   )}
										   tooltip={errors.has(BoxesDialogFields.PAGERANGE) ? Translate.value(
											   errors.get(BoxesDialogFields.PAGERANGE)!, {ns: "message"}
										   ) : ""}
										   options={[
											   {
												   label: Translate.value("inputs.pagerange.all"),
												   value: PAGERANGE_ALL
											   },
											   {
												   label: Translate.value("inputs.pagerange.current"),
												   value: this.state.currentPage
											   }
										   ]}/>
								{this.renderFieldError(errors.has(BoxesDialogFields.PAGERANGE) ?
									Translate.value(
										errors.get(BoxesDialogFields.PAGERANGE)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;

		let classList: Array<string> = ["boxes-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		if (parameter?.mode === BoxesFunction.REMOVE) {
			classList.push("large");
		} else {
			classList.push("verylarge");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} closeOnEscape={true}
					header={this.renderHeader()} appendTo={document.getElementById("root")} footer={this.renderFooter()}
					resizable={false}
					onShow={() => this.setState({
						renderPreview: true
					})}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.boxes.general"} namespace={"help"} markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.boxes.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
					{this.state.renderPreview && this.renderPreview()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: BoxesDialogFields, value: any): void {
		let parameter: PortalBoxesToolboxOptions = this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;
		let boxParameter: ResizeBoxes = this.getBoxParameter(parameter.type!)!;
		let errors: Map<BoxesDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case BoxesDialogFields.MODE:
				parameter.mode = value;
				break;
			case BoxesDialogFields.TYPE:
				parameter.type = value;
				break;
			case BoxesDialogFields.MARGIN_LEFT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = DimensionsBoxesResize.getXDefault();
				}

				boxParameter.boxesResize!.dimensions!.x = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BoxesDialogFields.MARGIN_TOP:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = DimensionsBoxesResize.getYDefault();
				}

				boxParameter.boxesResize!.dimensions!.y = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BoxesDialogFields.WIDTH:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = DimensionsBoxesResize.getWidthDefault();
				}

				boxParameter.boxesResize!.dimensions!.width = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BoxesDialogFields.HEIGHT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = DimensionsBoxesResize.getHeightDefault();
				}

				boxParameter.boxesResize!.dimensions!.height = UnitHelper.unitToUnit(
					value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
				);
				break;
			case BoxesDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				boxParameter.boxesResize!.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-boxes"}/>
					<Translate {...this.props} data="boxes.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;

		return (
			<div className="p-grid">
				<div className={"p-col-12 p-grid" +
					(parameter?.mode === BoxesFunction.REMOVE ? "" : " p-xl-8 divider-right-hidden")
				}>
					<fieldset className="p-col-12 p-grid" style={{
						flexGrow: 1
					}}>
						<div className="p-col-12 p-grid">
							<div className="p-col-12 p-grid">
								<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
										disabled={this.state.submitted} className="reset-button"
										onClick={() => this.resetParameter()}/>
								<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
										icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
										className="primary-button" onClick={() => this.submit()}/>
								{this.state.submitted ?
									<Button label={Translate.value("buttons.close")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/> :
									<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
											onClick={() => this.hide()} className="dialog-button"/>
								}
							</div>
						</div>
					</fieldset>
				</div>
				<div className="p-col-12 p-xl-4 p-lg-hidden p-grid divider-left-hidden"></div>
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		try {
			let mediaboxParameter: ResizeBoxes = this.getBoxParameter(PageBox.MediaBox)!;
			mediaboxParameter!.boxesResize = await this.getBoxDimensions(PageBox.MediaBox);

			let cropboxParameter: ResizeBoxes = await this.getBoxParameter(PageBox.CropBox)!;
			cropboxParameter!.boxesResize = await this.getBoxDimensions(PageBox.CropBox);

			let bleedboxParameter: ResizeBoxes = await this.getBoxParameter(PageBox.BleedBox)!;
			bleedboxParameter!.boxesResize = await this.getBoxDimensions(PageBox.BleedBox);

			let trimboxParameter: ResizeBoxes = await this.getBoxParameter(PageBox.TrimBox)!;
			trimboxParameter!.boxesResize = await this.getBoxDimensions(PageBox.TrimBox);

			let artboxParameter: ResizeBoxes = await this.getBoxParameter(PageBox.ArtBox)!;
			artboxParameter!.boxesResize = await this.getBoxDimensions(PageBox.ArtBox);

			this.setState({
				parameter: this.state.parameter,
				defaultParameter: this.state.parameter?.clone(),
				pages: await portalDocument.getNumberOfPages(),
				currentPage: documentSettings.activeViewer.currentPage.toString(),
				previewZoomValue: 0,
				unit: userSettings.unit
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.onShow();
			}
		}
	};

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.setState({
			renderPreview: false
		});

		super.hide();
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		let parameter: PortalBoxesToolboxOptions | undefined =
			this.state.parameter?.data?.boxes as PortalBoxesToolboxOptions;
		let boxParameter: ResizeBoxes | undefined = this.getBoxParameter(parameter.type!);

		if (parameter.mode === BoxesFunction.REMOVE) {
			parameter.remove!.push(RemoveBoxes.fromJson({
				boxesRemove: {
					box: parameter.type,
					pages: boxParameter?.boxesResize?.pages
				}
			} as RemoveBoxesInterface));
		}

		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let boxesOptionsParameter: PortalToolboxOptionsOptions = this.state.parameter!.data.clone();
			let boxesParameter: PortalBoxesToolboxOptions =
				boxesOptionsParameter.boxes as PortalBoxesToolboxOptions;
			if (boxesParameter.mode === BoxesFunction.REMOVE) {
				boxesParameter.resize = undefined;
			} else {
				boxesParameter.remove = undefined;
			}

			let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
				options: boxesOptionsParameter.toJson()
			});

			let operationData: Array<BaseToolbox> = [toolboxOperation];
			let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.toolbox.boxes", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
				this.hide();
			}
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BOXES;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxOptionsOptions> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const BoxesDialog = React.memo(BoxesDialogDefinition,
	(prevProps: Readonly<BoxesDialogProperties>, nextProps: Readonly<BoxesDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BoxesDialog;